import { useMemo } from 'react'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { ChartData, ChartOptions } from 'chart.js'
import { format } from 'date-fns'
import { Chart } from 'react-chartjs-2'

import {
  COLOR_REALLOCATION_PRIMARY,
  COLOR_REALLOCATION_SECONDARY,
  COLOR_REALLOCATION_TEXT_SECONDARY,
} from 'components/reports/Metrics/constants/AllocationStability'
import { DATE_DAY_MONTH_SHORT_FORMAT } from 'constants/index'

import {
  BAR_ALPHA,
  BAR_STYLE,
  COUNT_AXIS,
  createValueLabelsPlugin,
  LINE_STYLE,
  METRIC_CHART_HEIGHT,
  METRIC_CHART_SCALES,
  PERCENT_AXIS,
} from '../components/chartConfig'
import { LegendItem } from '../components/MetricCard/LegendItem'
import { AllocationStabilityPoint } from './useAllocationStabilityData'

interface Props {
  daily: AllocationStabilityPoint[]
  loading?: boolean
}

export const ReAllocatedOverTimeCard = ({ daily, loading }: Props) => {
  const { labels, counts, percentages, totals } = useMemo(
    () => ({
      labels: daily.map((p) => format(new Date(p.date), DATE_DAY_MONTH_SHORT_FORMAT)),
      counts: daily.map((p) => p.count),
      percentages: daily.map((p) => p.percentage),
      totals: daily.map((p) => p.totalJobs),
    }),
    [daily],
  )

  const data = useMemo<ChartData<'bar' | 'line', number[], string>>(
    () => ({
      labels,
      datasets: [
        {
          ...BAR_STYLE,
          type: 'bar' as const,
          label: 'Re-allocated Jobs (count)',
          data: counts,
          backgroundColor: alpha(COLOR_REALLOCATION_PRIMARY, BAR_ALPHA),
          yAxisID: COUNT_AXIS,
          order: 1,
        },
        {
          ...LINE_STYLE,
          type: 'line' as const,
          label: 'Re-allocated Jobs (%)',
          data: percentages,
          borderColor: COLOR_REALLOCATION_SECONDARY,
          backgroundColor: COLOR_REALLOCATION_SECONDARY,
          pointBackgroundColor: COLOR_REALLOCATION_SECONDARY,
          yAxisID: PERCENT_AXIS,
          order: 2,
        },
      ],
    }),
    [labels, counts, percentages],
  )

  const options = useMemo<ChartOptions<'bar' | 'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 24 } },
      scales: {
        ...METRIC_CHART_SCALES,
        [PERCENT_AXIS]: {
          ...METRIC_CHART_SCALES?.[PERCENT_AXIS],
          title: { display: true, text: 'Percentage of Jobs' },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) =>
              context.dataset.yAxisID === PERCENT_AXIS
                ? `Re-allocated Jobs (%): ${percentages[context.dataIndex]}%`
                : `Re-allocated Jobs: ${counts[context.dataIndex]} of ${totals[context.dataIndex]}`,
          },
        },
      },
    }),
    [counts, percentages, totals],
  )

  return (
    <Paper variant='outlined' sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignContent={'center'}
        sx={{ mb: 2 }}>
        <Box>
          <Typography
            variant='subtitle1'
            fontWeight={600}
            sx={{ color: COLOR_REALLOCATION_PRIMARY, mb: 0.25 }}>
            Re-allocated Jobs Over Time
          </Typography>
          <Typography variant='body2' color={COLOR_REALLOCATION_TEXT_SECONDARY}>
            Daily count and percentage of re-allocated jobs.
          </Typography>
        </Box>
        <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap' useFlexGap>
          <LegendItem
            label='Re-allocated Jobs (count)'
            swatch='bar'
            color={COLOR_REALLOCATION_PRIMARY}
          />
          <LegendItem
            label='Re-allocated Jobs (%)'
            swatch='line'
            color={COLOR_REALLOCATION_SECONDARY}
          />
        </Stack>
      </Stack>

      {loading ? (
        <Skeleton variant='rectangular' height={METRIC_CHART_HEIGHT} sx={{ borderRadius: 1 }} />
      ) : (
        <div style={{ height: METRIC_CHART_HEIGHT, position: 'relative' }}>
          <Chart<'bar' | 'line', number[], string>
            type='bar'
            data={data}
            options={options}
            plugins={[createValueLabelsPlugin<'bar' | 'line'>()]}
          />
        </div>
      )}

      <Stack
        direction='row'
        spacing={1}
        alignItems='flex-start'
        sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: '#f8f4fc' }}>
        <Box sx={{ color: 'text.secondary', display: 'flex', pt: 0.25 }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: COLOR_REALLOCATION_PRIMARY }} />
        </Box>
        <Box>
          <Typography variant='caption' color={COLOR_REALLOCATION_TEXT_SECONDARY} display='block'>
            A job is counted as re-allocated if it was allocated to one supplier and later allocated
            to a different supplier.
          </Typography>
          <Typography variant='caption' color={COLOR_REALLOCATION_TEXT_SECONDARY} display='block'>
            Only the latest re-allocation per job is counted.
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}
